class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      this.renderSectionFromFetch();
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    this.renderSectionFromFetch();
  }

  renderSectionFromFetch() {
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    const graphQLQuery = `
      query Search($query: String!, $first: Int!) {
        search(query: $query, first: $first, productFilters: {available: true}) {
          edges {
            node {
              ... on Product {
                title
                onlineStoreUrl
                featuredImage {
                  url(transform: {maxWidth: 100})
                }
                priceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
      }
    `;

    const variables = {
      query: this.getQuery() + '*',
      first: parseInt(this.dataset.resultsLimit || 5),
    };

    fetch(`/api/2024-04/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.Shopify.storefrontAccessToken,
      },
      body: JSON.stringify({
        query: graphQLQuery,
        variables: variables,
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        if (data.errors) {
          console.error('GraphQL Errors:', data.errors);
          throw new Error('GraphQL query failed');
        }
        
        const products = data.data.search.edges.map(edge => edge.node);
        
        const results = { products: products };
        this.renderGraphQLSearchResults(results);
        this.setAttribute('results', 'true');
      })
      .catch(error => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
      });
  }

  renderGraphQLSearchResults(results) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (!targetElement) {
      console.error(`Element with id '${id}' not found in the document.`);
      return;
    }

    let html = '';

    const hasProducts = results.products && results.products.length > 0;

    if (!hasProducts) {
      html = `<div class="search__box-item search__empty text-center"><p>該当商品なし。他の検索キーワードより再度検索をしてください</p></div>`;
    } else {
      if (hasProducts) {
        html += '<div class="search__box-item">';
        html += `<h4 class="text-h6">商品</h4>`;
        html += '<ul class="v-stack gap-4" role="list">';
        results.products.forEach(product => {
          const price = new Intl.NumberFormat(document.documentElement.lang, { style: 'currency', currency: product.priceRange.minVariantPrice.currencyCode }).format(product.priceRange.minVariantPrice.amount);
          html += `
            <li class="predictive-search-result__list-item">
                <a href="${product.onlineStoreUrl}" class="flex items-center gap-4 text-body">
                    ${product.featuredImage ? `<img src="${product.featuredImage.url}" alt="${product.title}" width="50" height="50" class="object-contain" loading="lazy">` : ''}
                    <div class="flex-grow">
                        <span>${product.title}</span>
                        <p class="m-0 text-sm text-subdued">${price}</p>
                    </div>
                </a>
            </li>`;
        });
        html += '</ul></div>';

        // ▼▼▼ 追加箇所 ▼▼▼
        const searchUrl = `/search?q=${encodeURIComponent(this.getQuery())}`;
        html += `
          <div class="search__results-all text-center">
            <a href="${searchUrl}" class="link">
              すべての検索結果をみる
            </a>
          </div>
        `;
        // ▲▲▲ 追加ここまで ▲▲▲
      }
    }

    this.searchContent?.classList.remove('hidden');
    targetElement.innerHTML = html;
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);